import { StorageAdapter, StorageCallback } from "../../storage/StorageAdapter";
import { buildListingPath } from "../../storage";
import { BaseStorageService } from "../StorageService";
import { ListingService } from "./ListingService";
import { StorageUploadFile } from "../../storage/common";
import { ILogger } from "../../utils/logger";

/** Takes care of all the media for listings */
export class StorageListingService extends BaseStorageService {
    constructor(
        protected listingId: string,
        protected listingService: ListingService,
        storageAdapter: StorageAdapter,
        logger?: ILogger,
    ) {
        super(storageAdapter, logger)
    }

    protected getBasePath(): string {
        return buildListingPath(this.listingId);
    }

    // Customize file size limit (20MB for listing media)
    protected getMaxFileSize(): number {
        return 20 * 1024 * 1024; // 20MB
    }

    // Only allow images and videos
    protected getAllowedMimeTypes(): string[] {
        return [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'video/mp4',
            'video/quicktime',
        ];
    }

    /**
     * Custom method to upload with listing context
     */
    async uploadForListing(
        buffer: StorageUploadFile,
        filename: string,
        callback?: StorageCallback
    ): Promise<{ id: string, path: string; url: string; size: number }> {
        const result = await this
            .upload(buffer, filename, callback, {
                listingId: this.listingId,
                originalFileName: filename,
            });

        // Track in database
        const docId = await this.listingService
            .listingPhotosService(this.listingId)
            .create({
                path: result.path,
                url: result.url,
                size: result.size,
                name: result.filename,
            });

        return { ...result, id: docId };
    }

    /**
     * Custom method to delete all listing media (only takes care of storage directory)
     */
    async deleteAllMedias(): Promise<void> {
        (await this.exists(this.getBasePath())) && (await this.deleteDirectory(this.getBasePath()));
    }

    /**
     * Custom method to delete a specific media
     * @param path The path of the file
     * @param id The id of the firebase ListingPhoto
     */
    async deleteMedia(path: string, id?: string): Promise<void> {
        try {
            !!id ?
                await this.listingService.listingPhotosService(this.listingId).delete(id)//trigger will take care of storage
                : await this.delete(path);
        }
        catch (e) { }
        finally { }
    }
}